import { CommonModule } from "@angular/common";
import { Component, inject, Input, OnInit } from "@angular/core";
import { FormGroup, FormsModule } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { credentialTableColumns } from "../../../core/table-templates/credential-table";
import { ZorroTableComponent } from "../../common/custom-table/zorro-table.component";
import { DynamicFormComponent } from "../../common/dynamic-form/dynamic-form.component";

@Component({
  selector: "app-add-new-app-modal",
  standalone: true,
  imports: [CommonModule, FormsModule, DynamicFormComponent, ZorroTableComponent],
  templateUrl: "./add-new-app-modal.component.html",
  styleUrl: "./add-new-app-modal.component.scss",
})
export class AddNewAppModalComponent implements OnInit {

  @Input() initialData: any = null;
  @Input() isEdit = false;

  activeModal = inject(NgbActiveModal);
  isActive = false;

  /** Reactive form owned by DynamicFormComponent */
  form = new FormGroup({});
  fields: any[] = [];

  /** Credentials table */
  credentialColumns = credentialTableColumns;
  credentialOptions = { showPagination: false };
  credentialRows: any[] = [
    { id: 1, role: "", username: "", password: "", remarks: "", isRowEditable: true },
  ];
  private nextCredId = 2;

  ngOnInit(): void {
    this.buildFields();
    if (this.initialData) {
      this.prefillCredentials();
    }
  }

  /** Sentinel sent to the backend when the user has NOT changed a credential's password. */
  static readonly PASSWORD_KEY = "<PASSWORD_KEY>";

  private prefillCredentials(): void {
    let creds = this.initialData?.credentials;
    if (typeof creds === "string") {
      try { creds = JSON.parse(creds); } catch { creds = []; }
    }
    if (Array.isArray(creds) && creds.length > 0) {
      this.credentialRows = creds.map((c: any, i: number) => ({
        id: i + 1,
        role: c.role ?? "",
        username: c.username ?? "",
        // Use sentinel so the backend knows to keep the existing password
        // if the user doesn't type a new one.
        password: AddNewAppModalComponent.PASSWORD_KEY,
        remarks: c.remarks ?? "",
        isRowEditable: false,
      }));
      this.nextCredId = this.credentialRows.length + 1;
    }
  }

  get isWebType(): boolean {
    return this.form.get("applicationType")?.value === "Web";
  }

  get isFormValid(): boolean {
    if (!this.form.valid) return false;
    if (this.isWebType) return this.credentialRows.length > 0;
    return true;
  }

  private buildFields(): void {
    const d = this.initialData ?? {};
    const browserVal = d.browser && d.browser !== "-" ? d.browser : "";

    this.fields = [
      // ── Always visible ──────────────────────────────────────────────────────
      {
        name: "Application Name",
        type: "text-input",
        key: "applicationName",
        id: "applicationName",
        placeholder: "Enter application name",
        wrapperClass: "col-md-6",
        value: d.name ?? "",
        validations: { required: true },
      },
      {
        name: "Application Type",
        type: "select",
        key: "applicationType",
        id: "applicationType",
        placeholder: "Select application type",
        wrapperClass: "col-md-6",
        value: d.paradigm ?? "",
        options: [
          { label: "Web",    value: "Web" },
          { label: "Mobile", value: "Mobile" },
        ],
        validations: { required: true },
      },

      // ── Web only ─────────────────────────────────────────────────────────────
      {
        name: "Browser",
        type: "select",
        key: "browser",
        id: "browser",
        placeholder: "Select browser",
        wrapperClass: "col-md-6",
        visibleIf: { key: "applicationType", value: "Web" },
        value: browserVal,
        options: [
          { label: "Chrome",  value: "Chrome" },
          { label: "Firefox", value: "Firefox" },
          { label: "Edge",    value: "Edge" }
        ],
        validations: { required: true },
      },
      {
        name: "Base URL",
        type: "text-input",
        key: "baseUrl",
        id: "baseUrl",
        placeholder: "Enter base URL",
        wrapperClass: "col-md-6",
        visibleIf: { key: "applicationType", value: "Web" },
        value: d.applicationUrl ?? d.baseUrl ?? "",
        validations: { required: true },
      },

      // ── Mobile only ───────────────────────────────────────────────────────────
      {
        name: "Type",
        type: "select",
        key: "mobileAppType",
        id: "mobileAppType",
        placeholder: "Type",
        wrapperClass: "col-md-6",
        visibleIf: { key: "applicationType", value: "Mobile" },
        value: d.mobileAppType ?? "",
        options: [
          { label: "Native",         value: "Native" },
          { label: "Hybrid",         value: "Hybrid" },
          { label: "Mobile-Web",     value: "Mobile-Web" },
          { label: "Cross-Platform", value: "Cross-Platform" },
        ],
        validations: { required: true },
      },
      {
        name: "Mobile Platform",
        type: "select",
        key: "mobilePlatform",
        id: "mobilePlatform",
        placeholder: "Mobile Platform",
        wrapperClass: "col-md-6",
        visibleIf: { key: "applicationType", value: "Mobile" },
        value: d.mobilePlatform ?? "",
        options: [
          { label: "Android", value: "ANDROID" },
          { label: "iOS",     value: "IOS" },
        ],
      },
      {
        name: "Upload APK",
        type: "file-upload",
        key: "mobileFileName",
        id: "mobileFileName",
        placeholder: "APK",
        wrapperClass: "col-md-12",
        visibleIf: { key: "applicationType", value: "Mobile" },
        configs: {
          fileTypes: ".apk, .ipa",
          buttonLabel: "Choose File",
          placeholderText: "APK and IPA files only",
        },
      },
    ];
  }

  addCredential(): void {
    this.credentialRows = [
      ...this.credentialRows,
      { id: this.nextCredId++, role: "", username: "", password: "", remarks: "", isRowEditable: true },
    ];
  }

  handleCredentialActions(event: any): void {
    const row = event?.data?.row;
    if (!row) return;

    switch (event.actionType) {
      case "edit":
        this.credentialRows = this.credentialRows.map((r) =>
          r.id === row.id ? { ...r, isRowEditable: true } : r
        );
        break;
      case "add": // save
        this.credentialRows = this.credentialRows.map((r) =>
          r.id === row.id ? { ...row, isRowEditable: false } : r
        );
        break;
      case "cancel":
        this.credentialRows = this.credentialRows.map((r) =>
          r.id === row.id ? { ...r, isRowEditable: false } : r
        );
        break;
      case "delete":
        this.credentialRows = this.credentialRows.filter((r) => r.id !== row.id);
        break;
    }
  }

  submit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    const payload: any = { ...this.form.value };
    if (this.isWebType) {
      payload.credentials = this.credentialRows.map((row) => {
        // Strip all UI-only keys injected by ZorroTable / row management
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, isRowEditable, level, expand, select, isDraft, ...rest } = row;
        // Also remove any dynamic show-password toggle keys ZorroTable appends (e.g. _showPassword_password)
        const c = Object.fromEntries(
          Object.entries(rest).filter(([k]) => !k.startsWith("_showPassword"))
        );
        // If editing and the password was not changed by the user, keep the sentinel.
        // Backend interprets "<PASSWORD_KEY>" as "do not update this password".
        return c;
      });
    }
    this.activeModal.close(payload);
  }
}
